import { useHistory } from '@/features/history/history';
import { Popover } from '@/components/ui/Popover';
import { cn } from '@/components/ui/cn';

/** Curved undo arrow; `flip` mirrors it into a redo arrow. */
const UndoArrow = ({ flip = false }: { flip?: boolean }) => (
  <svg
    width="16"
    height="16"
    // Artwork is bottom-heavy (arrowhead on the centerline, loop dropping to y≈14), so it
    // optically hangs low. Nudge the viewBox down ~1.6u to recenter it against the clock/moon.
    viewBox="0 1.6 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={flip ? { transform: 'scaleX(-1)' } : undefined}
  >
    <path d="M3 8h7a3 3 0 0 1 0 6H6" />
    <path d="M5.5 5.5 3 8l2.5 2.5" />
  </svg>
);

const HistoryIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
);

const iconBtn =
  'flex h-9 w-9 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg disabled:cursor-default disabled:text-fg-ghost disabled:hover:bg-transparent';

const listRow =
  'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors hover:bg-layer';

/**
 * Header history control: a single clock button whose dropdown holds the Undo / Redo actions
 * (⌘Z / ⌘⇧Z — stay open so you can step several times) above the full stack, which can jump
 * multiple steps at once. Reads the global stack from `useHistory`.
 */
export default function UndoRedo() {
  const { undo, redo, seek, canUndo, canRedo, undoLabel, redoLabel, past, future, busy } = useHistory();
  const shortcut = navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl+';

  const actionRow = 'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors hover:bg-layer disabled:cursor-default disabled:text-fg-ghost disabled:hover:bg-transparent';

  return (
    <Popover
      align="right"
      panelClassName="min-w-[260px] p-1"
      trigger={({ toggle, open }) => (
        <button
          type="button"
          className={cn(iconBtn, open && 'bg-layer-strong text-fg')}
          onClick={toggle}
          aria-label="History"
          title="History (undo / redo)"
        >
          <HistoryIcon />
        </button>
      )}
    >
      {(close) => {
        const P = past.length;
        const F = future.length;
        return (
          <div>
            {/* Undo / Redo actions — stay open so you can step several times; the list rows below jump. */}
            <button type="button" className={cn(actionRow, 'text-fg-soft')} disabled={!canUndo || busy} onClick={() => undo()}>
              <UndoArrow />
              <span className="min-w-0 flex-1 truncate">{canUndo ? `Undo ${undoLabel}` : 'Nothing to undo'}</span>
              <span className="shrink-0 text-[10px] tracking-wide text-fg-ghost">{shortcut}Z</span>
            </button>
            <button type="button" className={cn(actionRow, 'text-fg-soft')} disabled={!canRedo || busy} onClick={() => redo()}>
              <UndoArrow flip />
              <span className="min-w-0 flex-1 truncate">{canRedo ? `Redo ${redoLabel}` : 'Nothing to redo'}</span>
              <span className="shrink-0 text-[10px] tracking-wide text-fg-ghost">{shortcut}⇧Z</span>
            </button>
            {P || F ? <div className="my-1 h-px bg-line" /> : null}
            {!P && !F ? null : (
            <div className="max-h-[52vh] overflow-auto">
              {/* Furthest redo first, down to the next redo. */}
              {future
                .map((cmd, j) => ({ cmd, j }))
                .reverse()
                .map(({ cmd, j }) => (
                  <button
                    key={`f${j}`}
                    type="button"
                    className={cn(listRow, 'text-fg-muted')}
                    onClick={() => {
                      seek(P + j + 1);
                      close();
                    }}
                  >
                    <UndoArrow flip />
                    <span className="min-w-0 flex-1 truncate">{cmd.label}</span>
                    <span className="shrink-0 text-[10px] uppercase tracking-wide text-fg-ghost">redo</span>
                  </button>
                ))}

              <div className="my-1 flex items-center gap-2 px-2 text-[10px] font-semibold uppercase tracking-wide text-fg-ghost">
                <span className="h-px flex-1 bg-line" />
                current
                <span className="h-px flex-1 bg-line" />
              </div>

              {/* Most recent done action first, down to the oldest. */}
              {past
                .map((cmd, i) => ({ cmd, i }))
                .reverse()
                .map(({ cmd, i }) => (
                  <button
                    key={`p${i}`}
                    type="button"
                    className={cn(listRow, 'text-fg-soft')}
                    onClick={() => {
                      seek(i);
                      close();
                    }}
                    title={`Undo back to before "${cmd.label}"`}
                  >
                    <UndoArrow />
                    <span className="min-w-0 flex-1 truncate">{cmd.label}</span>
                  </button>
                ))}
            </div>
            )}
          </div>
        );
      }}
    </Popover>
  );
}
